import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

// TODO Sprint1: migrar a Supabase tabla tenant_preferences con tenant_id (post Sprint 0).

const MAX_FAVORITES = 8;
const MAX_RECENTS = 5;

type Prefs = {
  favorites: string[];
  recents: string[];
  collapsed: { favorites: boolean; recents: boolean };
};

const DEFAULT_PREFS: Prefs = {
  favorites: [],
  recents: [],
  collapsed: { favorites: false, recents: false },
};

const storageKey = (userId: string | null | undefined) =>
  `expai_sidebar_prefs_${userId ?? 'anon'}`;

function readPrefs(key: string): Prefs {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      favorites: Array.isArray(parsed.favorites) ? parsed.favorites.slice(0, MAX_FAVORITES) : [],
      recents: Array.isArray(parsed.recents) ? parsed.recents.slice(0, MAX_RECENTS) : [],
      collapsed: {
        favorites: parsed.collapsed?.favorites ?? false,
        recents: parsed.collapsed?.recents ?? false,
      },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(key: string, prefs: Prefs) {
  try {
    window.localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // ignore quota / serialization errors
  }
}

export interface UseSidebarPrefsResult {
  favorites: string[];
  recents: string[];
  toggleFavorite: (path: string) => void;
  addRecent: (path: string) => void;
  isFavorite: (path: string) => boolean;
  collapsed: { favorites: boolean; recents: boolean };
  setSectionCollapsed: (section: 'favorites' | 'recents', value: boolean) => void;
}

export function useSidebarPrefs(): UseSidebarPrefsResult {
  const { user } = useAuth();
  const key = storageKey(user?.id);
  const [prefs, setPrefs] = useState<Prefs>(() => readPrefs(key));

  // Reload when the storage key changes (login/logout)
  useEffect(() => {
    setPrefs(readPrefs(key));
  }, [key]);

  const update = useCallback(
    (updater: (prev: Prefs) => Prefs) => {
      setPrefs((prev) => {
        const next = updater(prev);
        writePrefs(key, next);
        return next;
      });
    },
    [key],
  );

  const isFavorite = useCallback(
    (path: string) => prefs.favorites.includes(path),
    [prefs.favorites],
  );

  const toggleFavorite = useCallback(
    (path: string) => {
      update((prev) => {
        if (prev.favorites.includes(path)) {
          return { ...prev, favorites: prev.favorites.filter((p) => p !== path) };
        }
        if (prev.favorites.length >= MAX_FAVORITES) {
          toast.error('Máximo 8 favoritos');
          return prev;
        }
        return { ...prev, favorites: [...prev.favorites, path] };
      });
    },
    [update],
  );

  const addRecent = useCallback(
    (path: string) => {
      update((prev) => {
        const filtered = prev.recents.filter((p) => p !== path);
        const recents = [path, ...filtered].slice(0, MAX_RECENTS);
        return { ...prev, recents };
      });
    },
    [update],
  );

  const setSectionCollapsed = useCallback(
    (section: 'favorites' | 'recents', value: boolean) => {
      update((prev) => ({ ...prev, collapsed: { ...prev.collapsed, [section]: value } }));
    },
    [update],
  );

  return {
    favorites: prefs.favorites,
    recents: prefs.recents,
    toggleFavorite,
    addRecent,
    isFavorite,
    collapsed: prefs.collapsed,
    setSectionCollapsed,
  };
}
